import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { AuthResponse, User, apiService } from '../services/api'
import { logger } from '../utils/logger'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>
  register: (email: string, password: string, firstName?: string, invitationToken?: string) => Promise<boolean>
  googleLogin: (credential: string, invitationToken?: string, options?: { silent?: boolean }) => Promise<boolean>
  logout: () => void
  updateUser: (userData: Partial<User>) => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

let currentUserInFlight: Promise<User> | null = null

function getCurrentUserCoalesced() {
  if (currentUserInFlight) {
    return currentUserInFlight
  }

  currentUserInFlight = apiService.getCurrentUser().finally(() => {
    currentUserInFlight = null
  })

  return currentUserInFlight
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  // Initialize auth state from localStorage (and refresh cookie if present)
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token')
        const savedUser = localStorage.getItem('user')

        if (token && savedUser) {
          try {
            const currentUser = await getCurrentUserCoalesced()
            setUser(currentUser)
            localStorage.setItem('user', JSON.stringify(currentUser))
            return
          } catch {
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            setUser(null)
            return
          }
        }

        // No access token but the HTTP-only refreshToken cookie may still be
        // valid (e.g. dev restart cleared localStorage, cross-tab logout).
        // Bypass the axios interceptor (which would redirect to /login on
        // failure) by calling refreshToken directly and swallowing errors.
        try {
          const refreshed = await apiService.refreshToken()
          if (refreshed.success && refreshed.data?.token) {
            localStorage.setItem('token', refreshed.data.token)
            if (refreshed.data.user) {
              localStorage.setItem('user', JSON.stringify(refreshed.data.user))
              setUser(refreshed.data.user)
            }
          }
        } catch {
          // No valid refresh cookie — remain anonymous.
        }
      } catch (error) {
        logger.error('Auth initialization error:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<boolean> => {
    try {
      setIsLoading(true)
      const response: AuthResponse = await apiService.login({ email, password, rememberMe })

      if (response.success) {
        const { token, user: userData } = response.data

        // Store token and user data
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(userData))
        setUser(userData)

        return true
      } else {
        toast.error(response.message || 'Login failed')
        return false
      }
    } catch (error: any) {
      logger.error('Login error:', error)
      toast.error(error.message || 'Login failed')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (
    email: string,
    password: string,
    firstName?: string,
    invitationToken?: string,
  ): Promise<boolean> => {
    try {
      setIsLoading(true)
      const response: AuthResponse = await apiService.register({ email, password, firstName, invitationToken })

      if (response.success) {
        const { token, user: userData } = response.data
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(userData))
        setUser(userData)
        return true
      } else {
        toast.error(response.message || 'Registration failed')
        return false
      }
    } catch (error: any) {
      logger.error('Registration error:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed'
      toast.error(errorMessage)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const googleLogin = async (
    credential: string,
    invitationToken?: string,
    options?: { silent?: boolean },
  ): Promise<boolean> => {
    try {
      setIsLoading(true)
      const response: AuthResponse = await apiService.googleAuth(credential, invitationToken)

      if (response.success) {
        const { token, user: userData } = response.data
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(userData))
        setUser(userData)
        return true
      } else {
        if (!options?.silent) {
          toast.error(response.message || 'Google authentication failed')
        }
        return false
      }
    } catch (error: any) {
      logger.error('Google auth error:', error)
      if (!options?.silent) {
        toast.error(error.error || error.message || 'Google authentication failed')
      }
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    // Call API logout (fire and forget)
    apiService.logout().catch(logger.error)

    // Clear local state immediately
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
  }

  const updateUser = async (userData: Partial<User>): Promise<boolean> => {
    try {
      const updatedUser = await apiService.updateProfile(userData)
      setUser(updatedUser)
      localStorage.setItem('user', JSON.stringify(updatedUser))
      return true
    } catch (error: any) {
      logger.error('Update user error:', error)
      toast.error(error.message || 'Failed to update profile')
      return false
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    googleLogin,
    logout,
    updateUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
